<template>
  <tr
    class="h-15"
    data-cy="table-row"
    :class="[
      {
        'cursor-pointer': showClickablePointer,
        'text-gray-300': isRowInactive,
        'border-b': hasBorder,
        'selected-row bg-primary/5': rowIsChecked,
        'child-row': isChild,
        'hover:bg-grey-hover': !rowIsChecked && selectAll,
        hidden: isRowHidden,
      },
      trClasses,
    ]"
    @click="handleRowClick"
  >
    <td
      v-if="selectAll || selectOne"
      class="px-5 font-normal"
      :class="tdClasses"
    >
      <div class="flex items-center">
        <lf-checkbox
          v-if="selectAll"
          :model-value="rowIsChecked"
          name="selectAll"
          :value="row.id"
          :disabled="disabledCheckboxes"
          @change="rowChanged"
        />
        <input
          v-if="selectOne"
          type="radio"
          name="selectOne"
          :value="`${row.id}_${row[selectOneKey]}`"
          :checked="selectOneChecked"
          :disabled="disabledCheckboxes"
          @click="radioSelected"
        />
        <table-select-details
          v-if="typeof selectDetailsProvider === 'function'"
          :options="selectDetailsProviderOptions"
        />
        <component
          v-else-if="typeof selectDetailsProvider === 'object'"
          :is="selectDetailsProvider"
          :data="row"
        />
      </div>
    </td>
    <table-row-node
      v-for="(column, index) in columns"
      :key="column.key"
      :row="row"
      :row-index="rowIndex"
      :column="column"
      :lazy-load="lazyLoad"
      :td-classes="tdClasses"
      :accordion-table="accordionTable"
      :no-wrap="column.nowrap"
      :is-last-column="index === columns.length - 1"
    />
  </tr>
  <table-row
    v-if="row.children?.length && row.open"
    :data="row.children"
    :columns
    is-child
    :lazy-load
    :row-click-condition="() => null"
  />
</template>
<script setup lang="ts">
import { type PropType, computed } from "vue";

import { useAuth } from "@/hooks/auth";

import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import TableRowNode from "@/components/ui/table/TableRowNode.vue";
import TableSelectDetails from "@/components/ui/table/TableSelectDetails.vue";

import type { IDealsColumn } from "@/models/applications";
import type {
  LazyLoadOptions,
  SelectDetailsProvider,
  TableSelectOptions,
} from "@/models/common";

const emit = defineEmits(["rowChanged", "row-clicked", "radioSelected"]);

const props = defineProps({
  columns: {
    type: Array as PropType<Array<IDealsColumn>>,
    default: null,
  },
  row: {
    type: Object,
    default: null,
  },
  rowIndex: {
    type: Number,
    default: -1,
  },
  selectAll: {
    type: Boolean,
    default: false,
  },
  selectOne: {
    type: Boolean,
    default: false,
  },
  selectOneKey: {
    type: String,
    default: "",
  },
  rowIsChecked: {
    type: Boolean,
    default: false,
  },
  selectOneChecked: {
    type: Boolean,
    default: false,
  },
  rowIsClickable: {
    type: Boolean,
    default: false,
  },
  rowIsInactive: {
    type: Function,
  },
  rowClickCondition: {
    type: Function,
    required: true,
  },
  selectDetailsProvider: {
    type: [Object, Function] as PropType<SelectDetailsProvider>,
    default: null,
  },
  accordionTable: {
    type: Boolean,
    default: false,
  },
  isChild: {
    type: Boolean,
    default: false,
  },
  hasBorder: {
    type: Boolean,
    default: true,
  },
  lazyLoad: {
    type: [Boolean, Object] as PropType<boolean | LazyLoadOptions>,
    default: true,
  },
  disabledCheckboxes: {
    type: Boolean,
    default: false,
  },
  getTrClasses: {
    type: Function,
  },
  getTdClasses: {
    type: Function,
  },
});

const { isSuperAdmin } = useAuth();

const trClasses = computed(() => {
  return props.getTrClasses?.(props.row) ?? "";
});
const tdClasses = computed(() => {
  return props.getTdClasses?.(props.row) ?? "";
});

const isRowInactive = computed(() => {
  return props.rowIsInactive?.(props.row) ?? false;
});

const selectDetailsProviderOptions = computed(() => {
  if (typeof props.selectDetailsProvider === "function") {
    return props.selectDetailsProvider(props.row);
  }
  return {} as TableSelectOptions;
});

const isRowHidden = computed(() => {
  if (!props.accordionTable || !props.row["open"]) {
    return false;
  }
  const rowIdFatherName = props.row.id.split("-")[0] || props.row.id;
  return props.row["fatherName"] === rowIdFatherName;
});

const showClickablePointer = computed(() => {
  return (
    props.rowIsClickable &&
    clickConditionFulfilled.value &&
    (!isRowInactive.value || isSuperAdmin) &&
    props.row.children?.length
  );
});

const handleRowClick = () => {
  if (clickConditionFulfilled.value) {
    emit("row-clicked", props.row.id);
  }
};

const rowChanged = (event: Event) => {
  emit(
    "rowChanged",
    (event.target as HTMLInputElement).checked,
    (event.target as HTMLInputElement).value
  );
};
const radioSelected = (event: Event) => {
  emit("radioSelected", (event.target as HTMLInputElement).value);
};

const clickConditionFulfilled = computed(() =>
  props.rowClickCondition(props.row)
);
</script>
